import type { FC } from "react";
import React, { useMemo } from "react";

import cn from "classnames";

import { extractProps } from "../../../../../../utils/react";
import {
    CounterInline,
    type CounterInlineProps,
} from "../../../../../CounterInline";
import { createDisplayNameForCoreHeader } from "../../utils/createDisplayName";
import { createTestIdForCoreHeader } from "../../utils/createTestId";

import type { PropsWithTsId } from "../../../../../../types/react";
import type {
    CustomTextView,
    ICoreHeaderSize,
    ICoreHeaderTextView,
} from "../../types";

import classes from "./CoreHeaderCounterInline.styl";

const COUNTER_INLINE_TEST_ID = createTestIdForCoreHeader("counter-inline");

type CommonHeaderCounterInline = Pick<
    CounterInlineProps,
    "quantity" | "maxQuantity"
> &
    ICoreHeaderTextView &
    Partial<PropsWithTsId>;

type CustomHeaderCounterInline = CustomTextView<CommonHeaderCounterInline>;

export type HeaderCounterInlineProps =
    | CommonHeaderCounterInline
    | CustomHeaderCounterInline;

export const CoreHeaderCounterInline: FC<HeaderCounterInlineProps> = (
    props,
) => {
    const {
        customColorsStyles,
        currentProps: {
            size,
            view,
            "data-tsid": tsid = COUNTER_INLINE_TEST_ID,
            ...restProps
        },
    } = useMemo(
        () =>
            extractProps(props as HeaderCounterInlineProps & ICoreHeaderSize, [
                "customColorsStyles",
            ]),
        [props],
    );

    const customVarsStyles = cn(classes["counter"], classes[`__size-${size!}`]);

    const customColorsStylesClassName = cn(
        classes["counter"],
        (view === "primary-on-color" || view === "custom") &&
            classes[`__view-${view!}`],
        customColorsStyles,
    );

    return (
        <CounterInline
            {...restProps}
            view={view === "primary" ? view : "custom"}
            customVarsStyles={customVarsStyles}
            customColorsStyles={customColorsStylesClassName}
            data-tsid={tsid}
        />
    );
};

CoreHeaderCounterInline.displayName =
    createDisplayNameForCoreHeader("CounterInline");

export default CoreHeaderCounterInline;
